import { CSSProperties, memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import numbro from 'numbro';

import { getGameTypeDetails } from '../../../constants/Games/gameType';
import { Icons, IconType } from '../../../constants/icons';
import { Images } from '../../../constants/images';
import { getPathBGByIndex } from '../../../constants/pathBackground';
import { SVGIconType } from '../../../constants/svgIcon';
import { colorForGameSetId } from '../../../constants/Theme/DefaultColors';
import { TranslationLocale } from '../../../i18n';
import { AlbumSummary } from '../../../models/RecordStore';
import { Space } from '../../../models/SpaceStore';
import { Album, AlbumAudience } from '../../../models/SpaceStore/Album';
import { Profile } from '../../../models/UserStore';
import { timeAgo } from '../../../utils/date';
import { getUiLocale } from '../../../utils/i18n';
import { getStringPrefixByLength } from '../../../utils/string';
import { AvatarNode } from '../AvatarNode';
import { ProgressBar } from '../ProgressBar';
import { SeparatorComponent } from '../SeparatorComponent';
import { Skeleton } from '../Skeleton';
import { SvgIcon } from '../SVGIcon';

import './AlbumCell.css';

export type AlbumCellProps = {
  album: Album;
  albumClassroom: Space | undefined | null;
  albumSummary?: AlbumSummary | null | undefined;
  classroomOwnerProfile?: Profile | undefined | null;
  textComponentStyle?: CSSProperties;
  onClick: () => void;
  onClickShare?: () => void;
};

export const AlbumCell = memo((props: AlbumCellProps) => {
  const {
    album,
    albumClassroom,
    albumSummary,
    classroomOwnerProfile,
    textComponentStyle,
    onClick,
    onClickShare,
  } = props;
  const { i18n } = useTranslation();

  const albumInfo =
    album?.i18nGameSetInfo.resources[album?.i18nGameSetInfo.defaultLocale];
  const gameTypeIcons = album?.content?.partIcons
    ? new Set(
        Object.values(album?.content?.partIcons)
          .filter((iconName) => iconName !== null)
          .map((iconName) =>
            iconName
              ? getGameTypeDetails(iconName).icon ?? IconType.GameLine
              : IconType.GameLine,
          ),
      )
    : undefined;
  const [isAlbumClassroomIconAvailable, setIsAlbumClassroomIconAvailable] =
    useState(true);

  const albumIcon = getPathBGByIndex(
    album?.content?.path[0]?.style.background.set ?? 0,
  );

  const completedGameForChallengeCount =
    albumSummary?.completedGameForChallengeCount ?? 0;
  const gameForChallengeCount = albumSummary?.gameForChallengeCount ?? 1;
  const playableCount = albumSummary?.playableCount ?? album.playableCount;
  const isNoGame = playableCount === 0; // album.statistics.gameCount === 0;
  const overallScore = Math.floor(
    albumSummary?.parts.overall.averageGameScore ?? 0,
  );

  const albumIconTitle = getStringPrefixByLength(
    albumInfo?.name.trim().replace('[快將解鎖] ', '') ?? '', // HARDCODE for this
    3,
  );

  return (
    <div className="album-cell">
      <div className="centered-container">
        <motion.div
          className="cell-row"
          onClick={onClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          whileHover={{ scale: 0.96 }}
          whileTap={{ scale: 0.96 }}
        >
          <div className="album-icon-component">
            <div className="album-icon-container">
              <SvgIcon
                name={SVGIconType.BubbleShape}
                color={colorForGameSetId(album.gameSetId)}
                className="bubble-shape"
              />
              <>
                <img
                  src={
                    album.icons[0]?.publicUrls?.medium ??
                    albumIcon ??
                    Images.speakingGameBackground
                  }
                  className="album-icon"
                />
                <div className="album-title-container">
                  <p className="album-title">{albumIconTitle}</p>
                  <div className="game-type-icon-container">
                    {gameTypeIcons && gameTypeIcons?.size > 0 && !isNoGame ? (
                      <div className="game-icon-container">
                        {Array.from(gameTypeIcons)
                          .slice(0, 4)
                          .map((iconType) => (
                            <img
                              key={iconType}
                              className="game-type-icon"
                              src={Icons[iconType]}
                              alt={iconType}
                            />
                          ))}
                      </div>
                    ) : null}
                    {gameTypeIcons && gameTypeIcons?.size > 0 && !isNoGame ? (
                      <div className="item-count-divider" />
                    ) : null}

                    {(album && album.statistics.itemCount) ||
                    album.statistics.itemCount === 0 ? (
                      <div className={'item-count-container'}>
                        <p className="item-count">{playableCount}</p>
                      </div>
                    ) : null}
                  </div>

                  {albumSummary?.isCompleted ? (
                    <div
                      className="complete-container"
                      style={{ marginTop: -4 }}
                    >
                      <SvgIcon
                        name="Complete"
                        width={20}
                        height={20}
                        color="#46CBC0"
                      />
                      <p className={'overall-score'}>{overallScore}%</p>
                    </div>
                  ) : gameForChallengeCount > 0 ? (
                    <ProgressBar
                      current={completedGameForChallengeCount}
                      total={gameForChallengeCount}
                      width="4rem"
                      height="0.5rem"
                      borderRadius="100px"
                      fillColor="#34A79C"
                      style={{ marginBottom: '6px' }}
                    />
                  ) : (
                    <ProgressBar
                      current={0}
                      total={1}
                      width="4rem"
                      height="0.5rem"
                      borderRadius="100px"
                      fillColor="#34A79C"
                      style={{ marginBottom: '6px' }}
                    />
                  )}
                </div>
              </>
              {onClickShare && (
                <SvgIcon
                  name={SVGIconType.Share}
                  className={`share-icon`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickShare();
                  }}
                />
              )}
            </div>
            {/* <div className="game-type-container">
              <div className="game-type-icon-container">
                {gameTypeIcons && gameTypeIcons?.size > 0 && !isNoGame ? (
                  <div className="game-icon-container">
                    {Array.from(gameTypeIcons)
                      .slice(0, 4)
                      .map((iconType) => (
                        <img
                          key={iconType}
                          className="game-type-icon"
                          src={Icons[iconType]}
                          alt={iconType}
                        />
                      ))}
                  </div>
                ) : null}

                {(album && album.statistics.itemCount) ||
                album.statistics.itemCount === 0 ? (
                  <div
                    className={
                      isNoGame
                        ? 'item-count-container-fat'
                        : 'item-count-container'
                    }
                  >
                    <p className="item-count">
                      {album.statistics.itemCount ?? 0}
                    </p>
                  </div>
                ) : null}
              </div>
            </div> */}
          </div>
          <div className="text-component" style={{ ...textComponentStyle }}>
            <p className="album-name">{albumInfo?.name}</p>
            <div className="info">
              <SeparatorComponent
                separator={<p className="separate-text">·</p>}
              >
                <div className="avatar-icon-container">
                  {classroomOwnerProfile ? (
                    <>
                      <AvatarNode profile={classroomOwnerProfile} size={32} />
                    </>
                  ) : albumClassroom?.icon?.publicUrls?.small &&
                    isAlbumClassroomIconAvailable ? (
                    <img
                      className="space-icon"
                      src={albumClassroom?.icon?.publicUrls?.small}
                      onError={() => setIsAlbumClassroomIconAvailable(false)}
                    />
                  ) : (
                    <Skeleton
                      width={'32px'}
                      height={'32px'}
                      borderRadius={'1rem'}
                    />
                  )}

                  {/* <p className="small-grey-text">
                    {
                      albumClassroom?.i18nClassroomInfo.resources[
                        albumClassroom.i18nClassroomInfo.defaultLocale
                      ]?.name
                    }
                  </p> */}
                </div>
                <p className="small-grey-text">
                  {timeAgo(
                    album.createdAt,
                    getUiLocale(i18n.language as TranslationLocale),
                  )}
                </p>

                <SvgIcon
                  className="privacy-icon"
                  name={
                    album.audience == AlbumAudience.Public
                      ? SVGIconType.Region
                      : album.audience == AlbumAudience.Members
                      ? SVGIconType.Member
                      : album.audience == AlbumAudience.Followers
                      ? SVGIconType.Friend
                      : SVGIconType.MembershipTier
                  }
                />

                <p className="small-grey-text">
                  <Trans
                    i18nKey="{count} Plays"
                    values={{
                      count: numbro(album.playCount).format({
                        thousandSeparated: true,
                      }),
                    }}
                  />
                </p>
              </SeparatorComponent>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
});
